import { useRef, useState } from "react";
import { accountService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";

export function useManageQuickBooksIntegrationViewModel() {
  const stateManager = useComponentState();
  const [quickBooksAuthConfig, setQuickBooksAuthConfig] = useState(null);
  const [qboItems, setQboItems] = useState([]);
  const [qboCustomFields, setQboCustomFields] = useState([]);
  const [qBOItemMappings, setQBOItemMappings] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const originalBankAccountsRef = useRef([]);

  const getQuickBooksAuthConfig = async () => {
    stateManager.run(async () => {
      const response = await accountService.quickBooksAuth.getConfig();

      if (response.status === HTTP_STATUSES.OK) {
        setQuickBooksAuthConfig(response.data);
      }
    });
  };

  const handleDisconnect = async () => {
    stateManager.run(async () => {
      const response = await accountService.quickBooksAuth.disconnect();

      if (response.status === HTTP_STATUSES.NO_CONTENT) {
        await getQuickBooksAuthConfig();
      }
    });
  };

  const getQboItems = async () => {
    stateManager.run(async () => {
      const response = await accountService.quickBooksItems.getAll();

      if (response.status === HTTP_STATUSES.OK) {
        setQboItems(response.data);
      }
    });
  };

  const getQboCustomFields = async () => {
    stateManager.run(async () => {
      const response = await accountService.quickBooksItems.getCustomFields();

      if (response.status === HTTP_STATUSES.OK) {
        setQboCustomFields(response.data);
      }
    });
  };

  const createQBOItemMapping = async (templateData) => {
    return stateManager.run(async () => {
      const response = await accountService.qBOItemMappings.create({
        data: templateData,
        toastMessageConfig: {
          success: { text: "QuickBooks mapping created successfully." },
          error: { text: "Failed to create QuickBooks mapping." },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setQBOItemMappings((prev) => [...prev, response.data]);
      }

      return response;
    });
  };

  const getQBOItemMappings = async () => {
    return stateManager.run(async () => {
      const response = await accountService.qBOItemMappings.getAll();

      if (response.status === HTTP_STATUSES.OK) {
        setQBOItemMappings(response.data);
      }

      return response;
    });
  };

  const updateQBOItemMapping = async (id, templateData, modifiedOn) => {
    return stateManager.run(async () => {
      const response = await accountService.qBOItemMappings.update({
        id,
        data: templateData,
        modifiedOn,
        toastMessageConfig: {
          success: { text: "QuickBooks mapping updated successfully." },
          error: { text: "Failed to update QuickBooks mapping." },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setQBOItemMappings((prev) =>
          prev.map((template) =>
            template.id === id ? { ...template, ...response.data } : template
          )
        );
      }

      return response;
    });
  };

  const deleteQBOItemMapping = async (item) => {
    return stateManager.run(async () => {
      const response = await accountService.qBOItemMappings.delete({
        id: item.id,
        modifiedOn: item.modifiedOn,

        toastMessageConfig: {
          success: { text: "QuickBooks mapping deleted successfully." },
          error: { text: "Failed to delete QuickBooks mapping." },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setQBOItemMappings((prev) =>
          prev.filter((template) => template.id !== item.id)
        );
      }

      return response;
    });
  };

  const getBankAccounts = async () => {
    return stateManager.run(async () => {
      const response = await accountService.bankAccounts.getFromQuickBooks();

      if (response.status === HTTP_STATUSES.OK) {
        setBankAccounts(response.data);
        originalBankAccountsRef.current = response.data;
      }

      return response;
    });
  };

  const updateBankAccount = async (id, data, modifiedOn) => {
    return stateManager.run(async () => {
      let response;

      const currentAccount = bankAccounts.find((a) => a.id === id);
      const isCurrentlyActive = currentAccount?.active;
      const willBeActive = data.active;
      const shouldDelete = isCurrentlyActive && willBeActive === false;

      if (shouldDelete) {
        response = await accountService.bankAccounts.remove({
          id,
          modifiedOn,
          toastMessageConfig: {
            success: { text: "Bank account updated successfully." },
            error: { text: "Failed to update bank account." },
          },
        });
      } else {
        const postData = {
          ...data,
          active:
            data.active !== undefined
              ? data.active
              : currentAccount?.active ?? true,
          acctNum: data.acctNum || "",
          fullyQualifiedName: data.fullyQualifiedName || "",
        };

        response = await accountService.bankAccounts.approve({
          data: postData,
          toastMessageConfig: {
            success: { text: "Bank account updated successfully." },
            error: { text: "Failed to update bank account." },
          },
        });
      }

      if (
        response.status === HTTP_STATUSES.OK ||
        response.status === HTTP_STATUSES.NO_CONTENT
      ) {
        setBankAccounts((prev) =>
          prev.map((account) => {
            if (account.id !== id) return account;

            let { active } = account;
            if (shouldDelete) {
              active = false;
            } else if (data.active !== undefined) {
              active = data.active;
            }

            return {
              ...account,
              ...data,
              active,
            };
          })
        );
      }

      return response;
    });
  };

  const updateLocalBankAccountState = (id, field, value) => {
    setBankAccounts((prev) =>
      prev.map((account) =>
        account.id === id ? { ...account, [field]: value } : account
      )
    );
  };

  const getOriginalBankAccount = (id) => {
    return originalBankAccountsRef.current.find((a) => a.id === id);
  };

  const creditMemoMappings = qBOItemMappings.filter(
    (m) => m.name === "Backcharge"
  );
  const customFieldMappings = qBOItemMappings.filter(
    (m) => m.name === "InvoiceBillingCommitmentCustomField"
  );

  return {
    isLoading: stateManager.isBusy(),
    quickBooksAuthConfig,
    getQuickBooksAuthConfig,
    handleDisconnect,
    getQboItems,
    qboItems,
    getQboCustomFields,
    qboCustomFields,
    createQBOItemMapping,
    getQBOItemMappings,
    updateQBOItemMapping,
    deleteQBOItemMapping,
    bankAccounts,
    getBankAccounts,
    updateBankAccount,
    updateLocalBankAccountState,
    getOriginalBankAccount,
    creditMemoMappings,
    customFieldMappings,
  };
}
